import Section from '@pelckmans/elementis/components/section';
import { Cell } from '@pelckmans/elementis/components/table';
import { useContext, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyState from '../../../../../shared-components/EmptyState';
import LoadingState from '../../../../../shared-components/LoadingState';
import { HorizontalScrollContextProvider } from '../../../../../shared-components/table/context/HorizontalScrollContext';
import ModuleContext from '../../../../material/_contexts/ModuleContext';
import { splitAssignmentsByEndDateCategory, splitAssignmentsByPastEndDateCategory } from '../../utils';
import useStudentAssignments, { type StudentAssignment } from '../hooks/useStudentAssignments';
import AssignmentDivision from './AssignmentDivision';

const BASE = 'PAGES.ASSIGNMENTS.STUDENT.SECTION';

type AssignmentsTableProps = {
  assignments: StudentAssignment[];
  finished?: boolean;
  fetchAssignments: () => Promise<void>;
  id: string;
};

function AssignmentsBody({
  assignments,
  fetchAssignments,
}: {
  assignments: StudentAssignment[];
  fetchAssignments: () => Promise<void>;
}) {
  const { assignmentsEndIn24hours, assignmentsEndIn7Days, assignmentsEndOver7Days } =
    splitAssignmentsByEndDateCategory(assignments);

  return (
    <>
      <AssignmentDivision
        endDateCategory="TODAY"
        assignments={assignmentsEndIn24hours}
        fetchAssignments={fetchAssignments}
      />
      <AssignmentDivision
        endDateCategory="THIS_WEEK"
        assignments={assignmentsEndIn7Days}
        fetchAssignments={fetchAssignments}
      />
      <AssignmentDivision
        endDateCategory="LATER"
        assignments={assignmentsEndOver7Days}
        fetchAssignments={fetchAssignments}
      />
    </>
  );
}

function FinishedAssignmentsBody({
  assignments,
  fetchAssignments,
}: {
  assignments: StudentAssignment[];
  fetchAssignments: () => Promise<void>;
}) {
  const {
    i18n: { language },
  } = useTranslation();

  const { endedThisWeek, endedLastWeek, endedByMonth } = splitAssignmentsByPastEndDateCategory(assignments, language);

  return (
    <>
      <AssignmentDivision
        endDateCategory="LAST_WEEK"
        assignments={endedThisWeek}
        finished
        fetchAssignments={fetchAssignments}
      />
      <AssignmentDivision
        endDateCategory="LAST_TWO_WEEKS"
        assignments={endedLastWeek}
        finished
        fetchAssignments={fetchAssignments}
      />
      {Object.entries(endedByMonth).map(([key, value]) => (
        <AssignmentDivision
          key={key}
          endDateCategory="IN_LAST_MONTH"
          assignments={value}
          monthAndYear={key}
          finished
          fetchAssignments={fetchAssignments}
        />
      ))}
    </>
  );
}

function AssignmentsTable({ assignments, finished, fetchAssignments, id }: AssignmentsTableProps) {
  const { t } = useTranslation();

  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  return (
    <HorizontalScrollContextProvider containerRef={containerRef} tableRef={tableRef}>
      <div className="scroll-table-container" ref={containerRef}>
        <table
          className="assignments-table student-assignments-table"
          data-testid="assignments-table"
          ref={tableRef}
          aria-labelledby={id}
        >
          <thead>
            <tr>
              <Cell.HeaderText title={t(`${BASE}.TABLE.HEADER.MATERIAL`)} sticky minWidth="xl" maxWidth="xl" />
              <Cell.HeaderText title={t(`${BASE}.TABLE.HEADER.SECTION`)} sticky minWidth="md" maxWidth="md" />
              <Cell.HeaderText
                title={finished ? t(`${BASE}.TABLE.HEADER.SUBMITTED`) : t(`${BASE}.TABLE.HEADER.PERIOD`)}
                sticky
                minWidth="sm"
                maxWidth="sm"
              />
              <Cell.HeaderText
                title={t(`${BASE}.TABLE.HEADER.RESULT`)}
                className="results-header-cell"
                sticky
                minWidth="xs"
                maxWidth="xs"
              />
              <Cell.HeaderText title={t(`${BASE}.TABLE.HEADER.ATTEMPTS`)} sticky minWidth="xs" maxWidth="xs" />
              <Cell.HeaderEmpty sticky className="actions-cell" />
            </tr>
          </thead>
          <tbody>
            {finished ? (
              <FinishedAssignmentsBody assignments={assignments} fetchAssignments={fetchAssignments} />
            ) : (
              <AssignmentsBody assignments={assignments} fetchAssignments={fetchAssignments} />
            )}
          </tbody>
        </table>
      </div>
    </HorizontalScrollContextProvider>
  );
}

function Assignments({ finished }: { finished?: boolean }) {
  const { t } = useTranslation();
  const { assignments, isLoading, fetchAssignments } = useStudentAssignments(finished);
  const { module } = useContext(ModuleContext);
  const id = useId();

  if (!module) return null;

  let content;

  if (isLoading) {
    content = <LoadingState variant="table" aria-label={t(`${BASE}.TABLE.EMPTY_STATE.A11Y_LABEL`)} />;
  } else if (assignments.length === 0) {
    content = (
      <EmptyState
        variant="table"
        id={id}
        headerTitle={finished ? t(`${BASE}.TABLE.EMPTY_STATE.FINISHED.TITLE`) : t(`${BASE}.TABLE.EMPTY_STATE.TITLE`)}
        bodyText={finished ? t(`${BASE}.TABLE.EMPTY_STATE.FINISHED.BODY`) : t(`${BASE}.TABLE.EMPTY_STATE.BODY`)}
      />
    );
  } else {
    content = (
      <AssignmentsTable assignments={assignments} finished={finished} fetchAssignments={fetchAssignments} id={id} />
    );
  }

  return (
    <Section
      title={finished ? t(`${BASE}.TITLE_FINISHED`) : t(`${BASE}.TITLE`)}
      className="student-assignments"
      contentClassName="content-wrapper"
      sectionLabelId={id}
    >
      {content}
    </Section>
  );
}

export default Assignments;
